// BUILD_VERSION: 20250628-1 (force chunk invalidation)
import { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";
import { normalizeToStringArray } from "@/lib/arrayUtils";
import { normalizeSmartSearchText } from "@shared/search/smartSearch";
import { parseMnemonicTagNames } from "@shared/tags/mnemonicTags";
import { crmTagClassName, crmTagRemoveClassName } from "@/components/ui/tagStyles";

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  suggestionsEndpoint?: string;
  suggestions?: string[];
  colored?: boolean;
  compact?: boolean;
  normalizeUppercase?: boolean;
  colorClassNameForTag?: (tag: string) => string;
  className?: string;
  fieldClassName?: string;
  inputClassName?: string;
}

const TAG_COLORS = [
  "border-sky-200 bg-sky-50 text-sky-800",
  "border-emerald-200 bg-emerald-50 text-emerald-800",
  "border-rose-200 bg-rose-50 text-rose-800",
  "border-amber-200 bg-amber-50 text-amber-800",
  "border-violet-200 bg-violet-50 text-violet-800",
  "border-teal-200 bg-teal-50 text-teal-800",
  "border-slate-200 bg-slate-50 text-slate-800",
  "border-orange-200 bg-orange-50 text-orange-800",
];

function colorForTag(tag: string) {
  const hash = tag.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
}

/**
 * Le tastiere touch e gli incolla non garantiscono un evento `keydown` per la
 * virgola. Separiamo quindi anche il valore ricevuto dall'input, così ogni
 * superficie che riusa TagInput crea sempre chip reali e non testo residuo.
 */
export function splitTagInputValue(value: string) {
  const parts = value.split(/[,;\n]/);
  return {
    completed: parts.slice(0, -1),
    remainder: parts.at(-1) ?? "",
    hasSeparator: parts.length > 1,
  };
}

export default function TagInput({
  value,
  onChange,
  disabled,
  placeholder,
  suggestionsEndpoint,
  suggestions: staticSuggestions,
  colored = false,
  compact = false,
  normalizeUppercase = false,
  colorClassNameForTag,
  className = "",
  fieldClassName = "",
  inputClassName = "",
}: TagInputProps) {
  const safeValue = normalizeToStringArray(value);
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    let current = true;
    if (!suggestionsEndpoint || disabled || !open) return () => controller.abort();
    const timer = window.setTimeout(async () => {
      try {
        const separator = suggestionsEndpoint.includes('?') ? '&' : '?';
        const res = await fetch(`${suggestionsEndpoint}${separator}q=${encodeURIComponent(input)}`, { signal: controller.signal });
        const nextSuggestions = res.ok ? await res.json() : [];
        if (current) setSuggestions(nextSuggestions);
      } catch {
        if (current && !controller.signal.aborted) setSuggestions([]);
      }
    }, 150);
    return () => {
      current = false;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [input, open, disabled, suggestionsEndpoint]);

  const appendTags = (tags: string[]) => {
    const existing = new Set(safeValue.map(normalizeSmartSearchText));
    const additions = parseMnemonicTagNames(tags, normalizeUppercase)
      .filter((tag) => {
        const key = normalizeSmartSearchText(tag);
        if (existing.has(key)) return false;
        existing.add(key);
        return true;
      });

    if (additions.length) onChange([...safeValue, ...additions]);
  };

  const addTag = (tag: string) => {
    appendTags([tag]);
    setInput("");
  };

  const handleInputChange = (nextValue: string) => {
    const { completed, remainder, hasSeparator } = splitTagInputValue(nextValue);
    if (!hasSeparator) {
      setInput(nextValue);
      return;
    }

    appendTags(completed);
    setInput(remainder);
  };

  const commitInput = () => {
    if (input.trim()) addTag(input);
  };

  const removeTag = (tag: string) => {
    const newTags = safeValue.filter((t) => t !== tag);
    onChange(newTags);
  };

  const handleRemoveClick = (e: React.MouseEvent, tag: string) => {
    e.stopPropagation();
    e.preventDefault();
    removeTag(tag);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (input.trim()) addTag(input);
    } else if (e.key === "Backspace" && !input && safeValue.length) {
      removeTag(safeValue[safeValue.length - 1]);
    }
  };

  const allSuggestions = [...(staticSuggestions ?? []), ...suggestions]
    .filter((tag, index, arr) => arr.findIndex((item) => normalizeSmartSearchText(item) === normalizeSmartSearchText(tag)) === index);
  const filtered = allSuggestions.filter(tag => !safeValue.some((current) => normalizeSmartSearchText(current) === normalizeSmartSearchText(tag)));

  return (
    <div className={`relative ${className}`}>
    <div
      className={`flex flex-wrap items-center gap-1 border border-border bg-background focus-within:ring-1 focus-within:ring-primary cursor-text ${compact ? "min-h-7 p-0.5" : "min-h-[38px] p-2"} ${fieldClassName}`}
      onClick={() => inputRef.current?.focus()}
    >
      {safeValue.map((tag) => (
        <span
          key={tag}
          data-ui={colorClassNameForTag ? undefined : "mnemonic-chip"}
          className={`${crmTagClassName} border ${colorClassNameForTag ? colorClassNameForTag(tag) : colored ? colorForTag(tag) : "border-transparent bg-primary/10 text-primary"}`}
        >
          <span className="min-w-0 truncate">{tag}</span>
          {!disabled && (
            <button
              type="button"
              onClick={(e) => handleRemoveClick(e, tag)}
              className={crmTagRemoveClassName}
              aria-label={`Rimuovi tag ${tag}`}
              title={`Rimuovi tag ${tag}`}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          onBlur={() => window.setTimeout(() => {
            commitInput();
            setOpen(false);
          }, 150)}
          placeholder={safeValue.length === 0 ? (placeholder ?? "Aggiungi tag…") : ""}
          className={`flex-1 outline-none bg-transparent ${compact ? "h-[22px] min-w-[48px] text-xs leading-[22px]" : "min-w-[72px] text-sm"} ${inputClassName}`}
        />
      )}
      {safeValue.length === 0 && disabled && (
        <span className="text-xs text-muted-foreground">Nessun tag</span>
      )}
      {!compact && <p className="w-full text-xs text-muted-foreground mt-0.5">Separa con virgola o esci dal campo per aggiungere</p>}
    </div>
    {open && filtered.length > 0 && (
      <div className="absolute z-50 mt-1 max-h-44 w-full overflow-y-auto border border-border bg-background shadow-md">
        {filtered.map(tag => (
          <button key={tag} type="button" onMouseDown={(event) => { event.preventDefault(); addTag(tag); }} className="block w-full px-3 py-2 text-left text-sm hover:bg-muted">
            {tag}
          </button>
        ))}
      </div>
    )}
    </div>
  );
}
